/**
 * Which language a public page is in, and on which host it is canonical (COMBI-4521).
 *
 * Dutch pages live on nl.combidesk.com and English ones on combidesk.com. src/.htaccess redirects the language
 * specific paths below to their host, so keep its path lists in sync with DUTCH_PATHS and ENGLISH_PATHS.
 * Paths that exist in both languages (/, /contact, /terms, /privacy, /apps) are in neither list.
 */

export type PageLanguage = "nl" | "en";

export interface AltRoutes {
  nl?: string;
  en?: string;
}

export const CANONICAL_ORIGINS: Record<PageLanguage, string> = {
  nl: "https://nl.combidesk.com",
  en: "https://combidesk.com"
};

const DUTCH_PATHS = /^\/(koppeling|koppelingen|softwarekoppelingen|klantenservice|over-combidesk|categorie|info\/webshop-beginnen|info\/boekhouden-voor-webshops)(\/|$)/;
const ENGLISH_PATHS = /^\/(integration|integrations|softwareintegrations|customerservice|about-combidesk|category|info\/start-webshop|info\/bookkeeping-for-webshops)(\/|$)/;

const PRODUCTION_HOSTS = /^((www|nl|en)\.)?combidesk\.com$/;

/** The language a path belongs to, or undefined for a path that exists in both languages. */
export function pathLanguage(path: string): PageLanguage | undefined {
  const cleanPath = path.split(/[?#]/)[0];
  if (DUTCH_PATHS.test(cleanPath)) {
    return "nl";
  }
  if (ENGLISH_PATHS.test(cleanPath)) {
    return "en";
  }
  return undefined;
}

/**
 * The language of a page: a route with an English alternative is a Dutch page and vice versa, otherwise the path
 * tells. Undefined for a page that exists in both languages under the same path.
 */
export function pageLanguage(path: string, altroutes?: AltRoutes): PageLanguage | undefined {
  if (altroutes?.en) {
    return "nl";
  }
  if (altroutes?.nl) {
    return "en";
  }
  return pathLanguage(path);
}

/**
 * App pages take the same parameters in both languages, so unlike other parameterized routes they have a fixed
 * alternative: /koppelingen/<api>/<key> is /integrations/<api>/<key> in English (and vice versa).
 */
export function appPageAltroutes(url: string): AltRoutes | undefined {
  const path = url.split(/[?#]/)[0];
  const dutch = path.match(/^\/koppelingen\/(.+)$/);
  if (dutch) {
    return {en: `integrations/${dutch[1]}`};
  }
  const english = path.match(/^\/integrations\/(.+)$/);
  return english ? {nl: `koppelingen/${english[1]}`} : undefined;
}

/**
 * The origin of a page's canonical URL. On the production hosts that's the host of the page's language - also when
 * the page is opened on another host. A page that exists in both languages under the same path (home, contact) is
 * canonical on the host of the language it's shown in. Other hosts (demo, localhost) keep their own origin.
 */
export function canonicalOrigin(location: { host: string; origin: string }, path: string, altroutes: AltRoutes | undefined,
                                currentLanguage: string): string {
  if (!PRODUCTION_HOSTS.test(location.host)) {
    return location.origin;
  }
  const language = pageLanguage(path, altroutes) ?? currentLanguage;
  return language === "nl" ? CANONICAL_ORIGINS.nl : CANONICAL_ORIGINS.en;
}

/**
 * The hreflang alternates of a page, on their canonical hosts. A language without an equivalent page is left out:
 * pointing it at the same path would point it at a URL that redirects (and the sitemaps are built from these tags).
 * x-default follows the English page, so it's absent when there is none.
 */
export function hreflangUrls(path: string, altroutes?: AltRoutes): { nl?: string; en?: string; "x-default"?: string } {
  const cleanPath = path.split(/[?#]/)[0];
  const language = pageLanguage(cleanPath, altroutes);
  const nl = language === "en"
      ? (altroutes?.nl ? `${CANONICAL_ORIGINS.nl}/${altroutes.nl}` : undefined)
      : CANONICAL_ORIGINS.nl + cleanPath;
  const en = language === "nl"
      ? (altroutes?.en ? `${CANONICAL_ORIGINS.en}/${altroutes.en}` : undefined)
      : CANONICAL_ORIGINS.en + cleanPath;
  return {nl, en, "x-default": en};
}
